import React, { useState, useEffect, useRef } from 'react';
import './PortfolioSection.css';
import resumeData from '../constants/resumeData.json';

const PortfolioSection = ({ section, onClose, nightMode = false }) => {
  const panelBodyRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  useEffect(() => {
    // Capture the ref value to use in cleanup
    const panelBodyElement = panelBodyRef.current;

    const checkScrollable = () => {
      if (panelBodyRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = panelBodyRef.current;
        const isScrollable = scrollHeight > clientHeight;
        // Only show if scrollable and at the top (not scrolled yet)
        setShowScrollIndicator(isScrollable && scrollTop === 0);
      }
    };

    // Check initially and after content loads
    checkScrollable();
    const timeoutId = setTimeout(checkScrollable, 100);

    // Hide indicator as soon as user starts scrolling
    const handleScroll = () => {
      if (panelBodyRef.current) {
        const { scrollTop } = panelBodyRef.current;
        if (scrollTop > 0) {
          setShowScrollIndicator(false);
        }
      }
    };

    if (panelBodyElement) {
      panelBodyElement.addEventListener('scroll', handleScroll);
    }

    // Check on resize
    window.addEventListener('resize', checkScrollable);

    return () => {
      clearTimeout(timeoutId);
      if (panelBodyElement) {
        panelBodyElement.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', checkScrollable);
    };
  }, [section]);
  const getSectionData = () => {
    switch (section) {
      case 'about':
        return {
          title: 'About',
          icon: '👤',
          content: (
            <div className="section-content">
              <div className="about-intro">
                <span className="about-tagline">AI Software Engineer</span>
                <p className="about-summary">
                  I’m Ujjwal, an AI Software Engineer building production AI agents and the software systems behind them. I launched Advanced Energy’s first production AI agent platform in 12 weeks using LangChain, LangGraph, Vertex AI, and Gemini, and built scalable backend services for a platform serving 300K+ monthly active users. As a Certified Scrum Product Owner (CSPO), I connect engineering decisions with the problems people need solved.
                </p>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-number">5+</span>
                    <span className="stat-label">Years Exp</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">300K+</span>
                    <span className="stat-label">Monthly Active Users</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">Startup + Enterprise</span>
                    <span className="stat-label">Experience</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">CSPO</span>
                    <span className="stat-label">Certified Product Owner</span>
                  </div>
                </div>
                <div className="about-highlights">
                  <span className="highlight-tag">AI Agents</span>
                  <span className="highlight-tag">LangChain</span>
                  <span className="highlight-tag">LangGraph</span>
                  <span className="highlight-tag">RAG</span>
                  <span className="highlight-tag">Vertex AI</span>
                  <span className="highlight-tag">Gemini</span>
                  <span className="highlight-tag">Python</span>
                  <span className="highlight-tag">FastAPI</span>
                  <span className="highlight-tag">Kafka</span>
                  <span className="highlight-tag">Kubernetes</span>
                </div>
              </div>
            </div>
          )
        };
      case 'education':
        return {
          title: 'Education',
          icon: '🎓',
          content: (
            <div className="section-content">
              <div className="education-list">
                <div className="education-item">
                  <img
                    className="education-logo"
                    src={`${process.env.PUBLIC_URL || ''}/workAssets/purdue.png`}
                    alt="Purdue University logo"
                  />
                  <div className="education-text">
                    <h3>Master of Business and Technology</h3>
                    <p><strong>Purdue University</strong> &mdash; West Lafayette, IN</p>
                    <span className="education-date-badge">Expected Dec 2026 · AI Innovations Fellowship · GPA: 3.71</span>
                    <p style={{ marginTop: '12px', marginBottom: '8px' }}><strong><em>Graduate Teaching Assistant</em></strong></p>
                    <ul style={{ marginTop: '8px', paddingLeft: '20px', textAlign: 'left' }}>
                      <li>Teaching Assistant for Python Programming, Data Visualization, and Database Management</li>
                      <li>Global Student Ambassador and Professional Development Committee leader</li>
                    </ul>
                  </div>
                </div>
                <div className="education-item">
                  <img
                    className="education-logo"
                    src={`${process.env.PUBLIC_URL || ''}/workAssets/ggsipu.png`}
                    alt="GGSIPU logo"
                  />
                  <div className="education-text">
                    <h3>Bachelor of Technology in Information Technology</h3>
                    <p><strong>Guru Gobind Singh Indraprastha University</strong> &mdash; Delhi, India</p>
                    <span className="education-date-badge">2015 &mdash; 2019 · GPA: 3.53</span>
                    <p style={{ marginTop: '8px' }}><strong>Founding Head, AI &amp; Blockchain Club</strong></p>
                    <p>Coursework: Data Structures and Algorithms, Operating Systems, C++, Java.</p>
                  </div>
                </div>
              </div>
            </div>
          )
        };
      case 'experience':
        return {
          title: 'Work Experience',
          icon: '💼',
          content: (
            <div className="section-content">
              <div className="experience-list">
                {resumeData.experience.map(job => (
                  <div className="experience-item" key={job.company}>
                    {job.logo && <img className={`experience-logo ${job.logo === 'advanced-energy.svg' ? 'ae-logo' : ''}`} src={`${process.env.PUBLIC_URL || ''}/workAssets/${job.logo}`} alt={`${job.company} logo`} />}
                    <div className="experience-text">
                      <h3>{job.role}</h3>
                      <p><strong>{job.company}</strong> &mdash; {job.location}</p>
                      <span className="date-badge">{job.period}</span>
                    </div>
                    <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                      {job.highlights.map(highlight => <li key={highlight}>{highlight}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )
        };
      case 'projects':
        return {
          title: 'Projects',
          icon: '🚀',
          content: (
            <div className="section-content">
              <div className="project-grid">
                {resumeData.projects.map(project => (
                  <div className="project-card" key={project.name}>
                    <h3>{project.name}</h3>
                    <span className="date-badge">{project.date}</span>
                    <p>{project.description}</p>
                    <div className="tech-stack">
                      {project.tech.map(tech => <span className="tech-tag" key={tech}>{tech}</span>)}
                    </div>
                  </div>
                ))}
                <div className="project-card">
                  <img
                    className="project-logo"
                    src={`${process.env.PUBLIC_URL || ''}/workAssets/Driver.png`}
                    alt="Driver Behavior AI Twin logo"
                  />
                  <h3>Driver Behavior AI Twin</h3>
                  <p>Engineered a real-time telemetry pipeline for mobile sensor streams and anomaly detection, and built a personalized AI Twin to learn behavioral baselines and generate adaptive safety insights.</p>
                  <div className="tech-stack">
                    <span className="tech-tag">Python</span>
                    <span className="tech-tag">Streaming</span>
                    <span className="tech-tag">ML</span>
                    <span className="tech-tag">Real-time</span>
                    <span className="tech-tag">Analytics</span>
                  </div>
                </div>
                <div className="project-card">
                  <img
                    className="project-logo"
                    src={`${process.env.PUBLIC_URL || ''}/workAssets/Pulss.png`}
                    alt="Pulse project logo"
                  />
                  <h3>Pulse &mdash; AI-Powered Healthcare Platform</h3>
                  <p>LLM-driven clinical workflows for symptom triage, virtual consultations, and medical report analysis, serving 5K+ users with secure HIPAA-compliant RESTful APIs.</p>
                  <div className="tech-stack">
                    <span className="tech-tag">Python</span>
                    <span className="tech-tag">LLMs</span>
                    <span className="tech-tag">LangChain</span>
                    <span className="tech-tag">REST APIs</span>
                    <span className="tech-tag">Healthcare AI</span>
                  </div>
                </div>
                <div className="project-card">
                  <img
                    className="project-logo"
                    src={`${process.env.PUBLIC_URL || ''}/workAssets/ThunderWallet.png`}
                    alt="Thunder Wallet logo"
                  />
                  <h3>Thunder Wallet &mdash; Non-Custodial Payments</h3>
                  <p>Secure multi-rail transactions via modular services and user-controlled key management.</p>
                  <span className="award-badge">🏆 Best UI/UX Award</span>
                  <div className="tech-stack">
                    <span className="tech-tag">Solidity</span>
                    <span className="tech-tag">React</span>
                    <span className="tech-tag">Node.js</span>
                    <span className="tech-tag">Web3.js</span>
                    <span className="tech-tag">TypeScript</span>
                  </div>
                </div>
                <div className="project-card">
                  <img
                    className="project-logo"
                    src={`${process.env.PUBLIC_URL || ''}/workAssets/Acquisense.png`}
                    alt="Acquisense platform logo"
                  />
                  <h3>Acquisense &mdash; AI Deal Intelligence</h3>
                  <p>
                    AI-powered due diligence and deal-sourcing for M&amp;A. Scores opportunities with financial signals and generates explainable risk summaries.
                  </p>
                  <div className="tech-stack">
                    <span className="tech-tag">Python</span>
                    <span className="tech-tag">OpenAI API</span>
                    <span className="tech-tag">FastAPI</span>
                    <span className="tech-tag">React</span>
                    <span className="tech-tag">PostgreSQL</span>
                  </div>
                </div>
                <div className="project-card">
                  <img
                    className="project-logo"
                    src={`${process.env.PUBLIC_URL || ''}/workAssets/benchmark.png`}
                    alt="Benchmarking tool logo"
                  />
                  <h3>Automated Benchmarking Tool</h3>
                  <p>Linux benchmarking tool with multithreaded parallel parsing &mdash; reduced release validation time by 80% for trading systems.</p>
                  <div className="tech-stack">
                    <span className="tech-tag">C++</span>
                    <span className="tech-tag">Python</span>
                    <span className="tech-tag">Linux</span>
                    <span className="tech-tag">Shell</span>
                    <span className="tech-tag">CI/CD</span>
                  </div>
                </div>
              </div>
            </div>
          )
        };
      case 'skills':
        return {
          title: 'Skills',
          icon: '⚡',
          content: (
            <div className="section-content">
              <div className="skills-container">
                {Object.entries(resumeData.skills).map(([category, items]) => (
                  <div className="skill-category" key={category}>
                    <h3>{category}</h3>
                    <div className="skill-tags">
                      {items.map(skill => <span className="skill-tag" key={skill}>{skill}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        };
      case 'certifications':
        return {
          title: 'Certifications & Honors',
          icon: '🏆',
          content: (
            <div className="section-content">
              <div className="certifications-list">
                <div className="certification-item">
                  <h3>Certified Scrum Product Owner (CSPO)</h3>
                  <p>Professional certification in Scrum product ownership.</p>
                </div>
                <div className="certification-item">
                  <h3>Competitions & Achievements</h3>
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    <li><a href="https://drive.google.com/file/d/1Y5QqRFMLorUSOB5uadf7b2z5bVUB4wMn/view" target="_blank" rel="noopener noreferrer" style={{ color: '#ff6b6b', textDecoration: 'underline' }}>ACM ICPC Regional Finalist (2016, 2018)</a></li>
                  </ul>
                </div>
                <div className="certification-item">
                  <h3>AWS Certifications</h3>
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    <li><a href="https://www.credly.com/badges/b5e7fc04-e66d-4b7b-95b3-b4576c7017b2" target="_blank" rel="noopener noreferrer" style={{ color: '#ff6b6b', textDecoration: 'underline' }}>Certified Cloud Practitioner</a></li>
                    <li><a href="https://www.credly.com/badges/97357130-cca0-4edf-bcb3-2f55a47bae80" target="_blank" rel="noopener noreferrer" style={{ color: '#ff6b6b', textDecoration: 'underline' }}>Certified AI Practitioner</a></li>
                  </ul>
                </div>
                <div className="certification-item">
                  <h3>Teaching & Leadership</h3>
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    <li><a href="https://drive.google.com/file/d/1Cf0A-iSpMMK0S518V2ak0ZTM_G6W3N2M/view" target="_blank" rel="noopener noreferrer" style={{ color: '#ff6b6b', textDecoration: 'underline' }}>Teaching Assistant &mdash; Data Structures &amp; Algorithms</a></li>
                    <li>Vice-president &mdash; Toastmasters International</li>
                    <li>Certified Yoga Teacher &mdash; Batch 2018</li>
                  </ul>
                </div>
              </div>
            </div>
          )
        };
      case 'contact':
        return {
          title: 'Contact',
          icon: '📧',
          content: (
            <div className="section-content">
              <div className="contact-available" style={{ marginTop: '12px' }}>
                <span className="contact-available-dot"></span>
                Open to new opportunities
              </div>
              <div className="contact-info">
                <div className="contact-item">
                  <span className="contact-icon">📧</span>
                  <a href="mailto:ujjwal451@gmail.com">ujjwal451@gmail.com</a>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">📱</span>
                  <a href="tel:+17654094910">+1 765-409-4910</a>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">💼</span>
                  <a href="https://www.linkedin.com/in/ujwlj/" target="_blank" rel="noopener noreferrer">linkedin.com/in/ujwlj</a>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">📍</span>
                  <span>West Lafayette, IN &mdash; Open to remote &amp; relocation</span>
                </div>
              </div>
              <div className="social-links">
                <a href="https://www.linkedin.com/in/ujwlj/" target="_blank" rel="noopener noreferrer" className="social-button">LinkedIn</a>
                <a href="mailto:ujjwal451@gmail.com" className="social-button">Email Me</a>
              </div>
            </div>
          )
        };
      default:
        return {
          title: 'Portfolio',
          icon: '📋',
          content: (
            <div className="section-content">
              <h2>Portfolio</h2>
              <p>Select a section to view details</p>
            </div>
          )
        };
    }
  };

  const sectionData = getSectionData();

  return (
    <div className={`portfolio-section ${nightMode ? 'night-mode' : ''}`}>
      <div className="section-panel">
        <div 
          className="panel-header"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL || ''}/coin.png)`,
          }}
        >
          <div className="mario-header">
            <div className="header-content">
              <span className="section-icon">{sectionData.icon}</span>
              <h1>{sectionData.title}</h1>
            </div>
            <button 
              className="close-button" 
              onClick={onClose}
              aria-label="Close panel"
            >
              ×
            </button>
          </div>
        </div>
        <div className="panel-body" ref={panelBodyRef}>
          {sectionData.content}
          {showScrollIndicator && (
            <div className="scroll-indicator">
              <div className="scroll-arrow">↓</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioSection;

